import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  TransactionState,
  TRANSACTION_FEATURE_KEY,
} from './transaction.reducer';
import {
  transactionStatusTranslationKey,
  type TransactionFilterChip,
} from '../models/transaction.model';
import { selectUserListUsers } from '@app/dashboard/pages/security-settings/user-access-management/store/user-access-management.selectors';

export const selectTransactionState = createFeatureSelector<TransactionState>(
  TRANSACTION_FEATURE_KEY
);

export const selectTransactions = createSelector(
  selectTransactionState,
  state => state.transactions
);

export const selectListLoading = createSelector(
  selectTransactionState,
  state => state.listLoading
);

export const selectListError = createSelector(
  selectTransactionState,
  state => state.listError
);

export const selectTotal = createSelector(
  selectTransactionState,
  state => state.total
);

export const selectPageIndex = createSelector(
  selectTransactionState,
  state => state.pageIndex
);

export const selectPageSize = createSelector(
  selectTransactionState,
  state => state.pageSize
);

export const selectActiveTab = createSelector(
  selectTransactionState,
  state => state.activeTab
);

export const selectSortActive = createSelector(
  selectTransactionState,
  state => state.sortActive
);

export const selectSortDirection = createSelector(
  selectTransactionState,
  state => state.sortDirection
);

export const selectFiltersByTab = createSelector(
  selectTransactionState,
  state => state.filtersByTab
);

export const selectCurrentFilters = createSelector(
  selectTransactionState,
  state => state.filtersByTab[state.activeTab] ?? {}
);

export const selectFilterModalOpen = createSelector(
  selectTransactionState,
  state => state.filterModalOpen
);

export const selectSearchTerm = createSelector(
  selectTransactionState,
  state => state.searchTerm
);

export const selectActiveFiltersCount = createSelector(
  selectCurrentFilters,
  filters => {
    if (!filters) return 0;
    let count = 0;
    if (filters.fromDate || filters.toDate) count++;
    if (filters.relatedVaultId) count++;
    if (filters.relatedWalletId) count++;
    if (filters.requesterIds?.length) count += filters.requesterIds.length;
    if (filters.status) count++;
    return count;
  }
);

/** Maps user id → display name from `userAccessManagement.userList` (GET `/api/users`). */
export const selectRequesterLabelMap = createSelector(
  selectUserListUsers,
  users => {
    const map: Record<string, string> = {};
    for (const u of users ?? []) {
      map[u.id] = `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.id;
    }
    return map;
  }
);

export const selectFilterChips = createSelector(
  selectCurrentFilters,
  selectRequesterLabelMap,
  (filters, requesterLabels): TransactionFilterChip[] => {
    const chips: TransactionFilterChip[] = [];
    if (filters.fromDate || filters.toDate) {
      chips.push({
        key: 'dateRange',
        label: '',
        dateFrom: filters.fromDate ?? null,
        dateTo: filters.toDate ?? null,
      });
    }
    if (filters.relatedVaultId) {
      chips.push({
        key: 'vault',
        label: '',
        translateKey: 'transactions.filter.chips.vault',
        translateParams: { id: filters.relatedVaultId },
        value: filters.relatedVaultId,
      });
    }
    if (filters.relatedWalletId) {
      chips.push({
        key: 'wallet',
        label: '',
        translateKey: 'transactions.filter.chips.wallet',
        translateParams: { id: filters.relatedWalletId },
        value: filters.relatedWalletId,
      });
    }
    (filters.requesterIds ?? []).forEach(id => {
      chips.push({
        key: 'requester',
        label: requesterLabels[id] ?? id,
        value: id,
      });
    });
    if (filters.status) {
      chips.push({
        key: 'status',
        label: filters.status,
        translateKey: transactionStatusTranslationKey(filters.status),
        value: filters.status,
      });
    }
    return chips;
  }
);

export const selectVisibleFilterChips = createSelector(
  selectFilterChips,
  chips => chips.slice(0, 3)
);

export const selectExtraFilterCount = createSelector(
  selectFilterChips,
  chips => (chips.length > 3 ? chips.length - 3 : 0)
);

/** Detail */
export const selectDetailLoading = createSelector(
  selectTransactionState,
  state => state.detailLoading
);

export const selectDetailError = createSelector(
  selectTransactionState,
  state => state.detailError
);

export const selectTransactionDetail = createSelector(
  selectTransactionState,
  state => state.detail
);

export const selectDetailSelectedStep = createSelector(
  selectTransactionState,
  state => state.detailSelectedStep
);

export const selectDetailApprovalRequestId = createSelector(
  selectTransactionDetail,
  detail => detail?.data?.approvalRequestId ?? null
);

export const selectApprovalProgress = createSelector(
  selectTransactionState,
  state => state.approvalProgress
);

export const selectApprovalProgressLoading = createSelector(
  selectTransactionState,
  state => state.approvalProgressLoading
);

export const selectApprovalProgressError = createSelector(
  selectTransactionState,
  state => state.approvalProgressError
);

export const selectTransactionStep01DetailsRaw = createSelector(
  selectTransactionState,
  state => state.transactionStep01Details
);

export const selectTransactionStep01DetailsLoading = createSelector(
  selectTransactionState,
  state => state.transactionStep01DetailsLoading
);

export const selectTransactionStep01DetailsError = createSelector(
  selectTransactionState,
  state => state.transactionStep01DetailsError
);

export const selectTransactionStep01ViewModel = createSelector(
  selectTransactionStep01DetailsRaw,
  selectTransactionDetail,
  (raw, _detail) => raw
);

export const selectDetailProjectionState = createSelector(
  selectTransactionDetail,
  detail => detail?.data?.status?.state ?? null
);

export const selectBroadcastSubmitting = createSelector(
  selectTransactionState,
  state => state.broadcastSubmitting
);

/* Step 3 "Choose fee & Execute" (Part D). Verification UX lives in the shared
 * `verification` slice — consumers check `selectVerificationSource === 'execute'`. */
export const selectFeeOptions = createSelector(
  selectTransactionState,
  state => state.feeOptions
);

export const selectFeeOptionsLoading = createSelector(
  selectTransactionState,
  state => state.feeOptionsLoading
);

export const selectFeeOptionsError = createSelector(
  selectTransactionState,
  state => state.feeOptionsError
);

export const selectExecuteSubmitting = createSelector(
  selectTransactionState,
  state => state.executeSubmitting
);

export const selectPendingExecuteFeeChoice = createSelector(
  selectTransactionState,
  state => state.pendingExecuteFeeChoice
);

export const selectCancelSubmitting = createSelector(
  selectTransactionState,
  state => state.cancelSubmitting
);

export const selectPendingCancelReason = createSelector(
  selectTransactionState,
  state => state.pendingCancelReason
);

/*
 * Broadcast verification challenge & OTP submission state now live in
 * the shared `verification` slice (`@shared/store/verification`). Read
 * `selectVerificationActionId`, `selectVerificationMethod`,
 * `selectVerificationSubmitting`, `selectVerificationError`, and
 * `selectVerificationAttemptsRemaining` from there instead. Step-04
 * consumers should additionally check `selectVerificationSource === 'broadcast'`
 * to ignore challenges originating from other features.
 */

export const selectDetailWalletDisplayName = createSelector(
  selectTransactionDetail,
  detail => detail?.data.wallet.name ?? ''
);

export const selectDetailVaultName = createSelector(
  selectTransactionDetail,
  detail => detail?.data.wallet.vaultName ?? ''
);

/** Signing (step 3 + stepper) */
export const selectSigningLoading = createSelector(
  selectTransactionState,
  state => state.signingLoading
);

export const selectSigningError = createSelector(
  selectTransactionState,
  state => state.signingError
);

export const selectSigningStepStatus = createSelector(
  selectTransactionState,
  state => state.signingStepStatus
);

export const selectSigningUpdatedAt = createSelector(
  selectTransactionState,
  state => state.signingUpdatedAt
);

export const selectSigningChannels = createSelector(
  selectTransactionState,
  state => state.signingChannels
);

/** Network step (step 4) */
export const selectNetworkLoading = createSelector(
  selectTransactionState,
  state => state.networkLoading
);

export const selectNetworkError = createSelector(
  selectTransactionState,
  state => state.networkError
);

export const selectNetworkDetails = createSelector(
  selectTransactionState,
  state => state.networkDetails
);

export const selectBlockchainDetailsLoading = createSelector(
  selectTransactionState,
  state => state.blockchainDetailsLoading
);

export const selectBlockchainDetailsError = createSelector(
  selectTransactionState,
  state => state.blockchainDetailsError
);

export const selectBlockchainDetails = createSelector(
  selectTransactionState,
  state => state.blockchainDetails
);

/** Create transaction flow */

export const selectCreateWithdrawalSubmitting = createSelector(
  selectTransactionState,
  state => state.createWithdrawalSubmitting
);

export const selectCreateWithdrawalError = createSelector(
  selectTransactionState,
  state => state.createWithdrawalError
);

export const selectCreateWithdrawalResponse = createSelector(
  selectTransactionState,
  state => state.createWithdrawalResponse
);

export const selectCreateWithdrawalStatusCode = createSelector(
  selectTransactionState,
  state => state.createWithdrawalStatusCode
);

export const selectCreateWithdrawalPrefill = createSelector(
  selectTransactionState,
  state => state.createWithdrawalPrefill
);

export const selectCreateFlowNetworkFee = createSelector(
  selectTransactionState,
  state => state.createFlowNetworkFee
);

export const selectCreateFlowNetworkFeeLoading = createSelector(
  selectTransactionState,
  state => state.createFlowNetworkFeeLoading
);

export const selectCreateFlowNetworkFeeError = createSelector(
  selectTransactionState,
  state => state.createFlowNetworkFeeError
);

export const selectDetailWithdrawalLimitSummary = createSelector(
  selectTransactionState,
  state => state.detailLimitSummary
);

export const selectCreateFlowWithdrawalLimitSummary = createSelector(
  selectTransactionState,
  state => state.createFlowWithdrawalLimitSummary
);

export const selectCreateFlowWithdrawalLimitSummaryLoading = createSelector(
  selectTransactionState,
  state => state.createFlowWithdrawalLimitSummaryLoading
);

export const selectCreateFlowWithdrawalLimitSummaryError = createSelector(
  selectTransactionState,
  state => state.createFlowWithdrawalLimitSummaryError
);

export const selectCreateFlowPricingRateByBaseSymbol = createSelector(
  selectTransactionState,
  state => state.createFlowPricingRateByBaseSymbol
);

export const selectCreateFlowPricingRateLoadingByBaseSymbol = createSelector(
  selectTransactionState,
  state => state.createFlowPricingRateLoadingByBaseSymbol
);

export const selectCreateFlowPricingRateErrorByBaseSymbol = createSelector(
  selectTransactionState,
  state => state.createFlowPricingRateErrorByBaseSymbol
);

export const selectCreateFlowUsdtRateForBaseSymbol = (baseSymbol: string) =>
  createSelector(selectCreateFlowPricingRateByBaseSymbol, map => {
    const key = String(baseSymbol ?? '').toUpperCase();
    return map?.[key] ?? null;
  });

export const selectCreateFlowUsdtRateLoadingForBaseSymbol = (
  baseSymbol: string
) =>
  createSelector(selectCreateFlowPricingRateLoadingByBaseSymbol, map => {
    const key = String(baseSymbol ?? '').toUpperCase();
    return Boolean(map?.[key]);
  });

export const selectCreateFlowUsdtRateErrorForBaseSymbol = (
  baseSymbol: string
) =>
  createSelector(selectCreateFlowPricingRateErrorByBaseSymbol, map => {
    const key = String(baseSymbol ?? '').toUpperCase();
    return map?.[key] ?? null;
  });
